/**
 * ===================================================================================================
 * MemberListKakao.js - 카카오톡 스타일 조원 목록 컴포넌트 (모바일 최적화)
 * ===================================================================================================
 *
 * 📍 위치: 프론트엔드 > client/src/components/coordination
 *
 * 🎯 주요 기능:
 *    - 카카오톡 대화방 스타일의 조원 목록 UI
 *    - 프로필 아바타 (이름 첫 글자 또는 색상 원형)
 *    - 모바일에 최적화된 터치 인터페이스
 *    - 간결하고 깔끔한 디자인
 *
 * 🔗 연결된 파일:
 *    - ../../utils/coordinationUtils - 조원 이름 표시 유틸리티
 *    - ConversationalRoomView.js - 이 컴포넌트를 사용하는 상위 컴포넌트
 *
 * ===================================================================================================
 */

import React from 'react';
import { Crown, Plus, User, UserPlus } from 'lucide-react';
import { getMemberDisplayName, isCurrentUser, isMemberOwner } from '../../utils/coordinationUtils';

/**
 * 이름에서 첫 글자 추출 (한글/영문)
 */
const getInitial = (name) => {
  if (!name) return '?';
  return name.charAt(0).toUpperCase();
};

/**
 * MemberItemKakao - 카카오톡 스타일 조원 아이템
 */
const MemberItemKakao = ({
  member,
  currentRoom,
  user,
  isOwner,
  onMemberClick,
  onNicknameClick,
  index,
  friends,
  onAddFriend,
  localNicknames,
}) => {
  const memberData = member.user || member;
  const isCurrentUserMember = isCurrentUser(memberData, user);
  // 닉네임 우선순위: 내가 설정한 로컬 닉네임 > 본인이 설정한 방 닉네임 > 기본 이름
  const memberId = (memberData._id || memberData.id)?.toString();
  const localNick = localNicknames?.[memberId];
  const serverNick = member.nickname;
  const memberName = localNick || serverNick || getMemberDisplayName(memberData);
  // 대화형 모드에서는 방장 개념 없음 → 왕관 표시 안 함
  const isConversational = currentRoom.mode === 'conversational';
  const memberIsOwner = !isConversational && isMemberOwner(memberData, currentRoom);

  const friendIds = new Set((friends || []).map(f => (f._id || f.id)?.toString()));
  const isFriend = friendIds.has(memberId);

  return (
    <div
      className="flex items-center px-5 py-3 cursor-pointer active:bg-gray-50"
      onClick={() => onNicknameClick
        ? onNicknameClick(memberData)
        : onMemberClick && onMemberClick(memberData._id || memberData.id)
      }
    >
      {/* 프로필 아바타 */}
      <div className="relative flex-shrink-0">
        <div className="w-14 h-14 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center flex-shrink-0">
          {memberData.profileImage ? (
            <img src={memberData.profileImage} alt={memberName} className="w-full h-full object-cover" />
          ) : (
            <User size={28} className="text-gray-400" />
          )}
        </div>
        {/* 방장 표시 - 표준 모드에서만 */}
        {memberIsOwner && (
          <div className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
            <Crown size={12} className="text-white fill-white" />
          </div>
        )}
      </div>

      {/* 조원 정보 */}
      <div className="flex-1 ml-4 min-w-0">
        <div className="flex items-center gap-2">
          {/* 본인 표시 */}
          {isCurrentUserMember && (
            <span className="text-sm px-1.5 py-0.5 bg-gray-200 text-gray-700 rounded flex-shrink-0 font-medium">
              나
            </span>
          )}

          {/* 이름 */}
          <span className="text-base font-normal text-gray-900 truncate">
            {memberName}
          </span>
        </div>
      </div>

      {/* 친구 추가 버튼 (비친구 + 본인 아닌 경우) */}
      {!isCurrentUserMember && !isFriend && onAddFriend && (
        <button
          onClick={(e) => { e.stopPropagation(); onAddFriend(memberId); }}
          className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 text-xs font-semibold rounded-full transition-colors border border-blue-100"
          title="친구 추가"
        >
          <UserPlus size={13} />
          추가
        </button>
      )}
    </div>
  );
};

/**
 * MemberListKakao - 카카오톡 스타일 조원 목록 컨테이너
 */
const MemberListKakao = ({
  currentRoom,
  user,
  isOwner,
  onMemberClick,
  onMemberScheduleClick,
  onInvite,
  friends,
  onAddFriend,
  localNicknames,
  onNicknameClick,
}) => {
  const memberCount = (currentRoom.members || []).length;

  return (
    <div className="flex flex-col h-full bg-white">
      {/* 헤더 */}
      <div className="px-5 py-4 flex-shrink-0">
        <h3 className="text-sm font-medium text-gray-600">
          대화상대 {memberCount}
        </h3>
      </div>

      {/* 조원 목록 */}
      <div className="flex-1 overflow-y-auto">
        {/* 초대하기 버튼 - 멤버 아이템과 동일한 스타일 */}
        {onInvite && (
          <div
            className="flex items-center px-5 py-3 cursor-pointer active:bg-gray-50"
            onClick={onInvite}
          >
            <div className="relative flex-shrink-0">
              <div className="w-14 h-14 rounded-full flex items-center justify-center bg-gray-100 border-2 border-dashed border-gray-300">
                <Plus size={24} className="text-gray-400" />
              </div>
            </div>
            <div className="flex-1 ml-4 min-w-0">
              <span className="text-base font-normal text-gray-500">
                초대하기
              </span>
            </div>
          </div>
        )}

        {(currentRoom.members || []).map((member, index) => (
          <MemberItemKakao
            key={member.user?._id || member._id || index}
            member={member}
            currentRoom={currentRoom}
            user={user}
            isOwner={isOwner}
            onMemberClick={onMemberClick}
            onNicknameClick={onNicknameClick}
            index={index}
            friends={friends}
            onAddFriend={onAddFriend}
            localNicknames={localNicknames}
          />
        ))}
      </div>
    </div>
  );
};

export default MemberListKakao;
